import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Tooltip } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import { getUniformItemsApi, deleteUniformItemApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import UniformItemFormModal from './UniformItemFormModal';
import DeleteModal from 'components/modelpopup/DeleteModal';
import PageHeader from 'components/PageHeader';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { uniformGenderOptions } from '../../constants';
import { useNavigate } from 'react-router-dom';
import useStickyIdentity from '../../components/useStickyIdentity';

const UniformItemList = () => {
  const { sizeOptions, fetchSizes } = useUniformMgmt();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    getUniformItemsApi({
      page: pageSettings.page,
      page_size: pageSettings.pageSize,
      search: searchTerm,
      sort_by: pageSettings.sortBy,
      sort_type: pageSettings.sortType,
    }).then(response => {
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [pageSettings.page, pageSettings.pageSize, pageSettings.sortBy, pageSettings.sortType, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    let pageData = handlePageChange(pagination, filters, sorter, pageSettings);
    setPageSettings(pageData);
  };

  const handleEdit = (record) => {
    setSelectedItem(record);
    setShowModal(true);
  };

  const handleDelete = () => {
    deleteUniformItemApi(deleteId).then(() => {
      toast.success('Uniform item deleted', { position: "top-right", autoClose: 3000, transition: Bounce });
      setDeleteModal(false);
      setDeleteId(null);
      fetchData();
    }).catch(() => {
      toast.error('Failed to delete item', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const handleSuccess = () => {
    setShowModal(false);
    setSelectedItem(null);
    fetchData();
  };

  const getGenderLabel = (val) => {
    return uniformGenderOptions.find(o => o.value === val)?.label || val;
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Pinned: eight columns, and the item name is the row's identity.
      title: 'Name',
      dataIndex: 'name',
      sorter: true,
      ...pinLeft(220),
      render: (text, record) => (
        <a onClick={() => navigate(`/uniform-management/items/${record.id}`)} style={{ cursor: 'pointer', color: '#D3001A' }}>
          {text}
        </a>
      )
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      render: (val) => getGenderLabel(val)
    },
    {
      title: 'Color',
      dataIndex: 'color',
      render: (color) => color ? (
        <div className="d-flex align-items-center gap-2">
          <span style={{
            width: 14, height: 14, borderRadius: '50%',
            backgroundColor: color.toLowerCase(), border: '1px solid #ddd', display: 'inline-block'
          }} />
          {color}
        </div>
      ) : '-'
    },
    {
      title: 'Rotation (months)',
      dataIndex: 'rotation_cycle_months',
      render: (val) => val || '-'
    },
    {
      title: 'Sizes',
      dataIndex: 'sizes',
      render: (sizes) => {
        if (!sizes?.length) return '-';
        const displayed = sizes.slice(0, 3);
        const remaining = sizes.length - 3;
        return (
          <div>
            {displayed.map(s => <Tag key={s.id}>{s.code}</Tag>)}
            {remaining > 0 && (
              <Tooltip title={sizes.slice(3).map(s => s.code).join(', ')}>
                <Tag>+{remaining}</Tag>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: 'Positions',
      dataIndex: 'position_count',
      render: (count) => count || 0
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => (
        <span className={`badge ${status ? 'bg-success-light' : 'bg-danger-light'}`}>
          {status ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      title: 'Action',
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button icon="fa fa-pencil" className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(record)} />
          <Button icon="fa fa-trash" className="btn btn-sm btn-outline-danger" onClick={() => { setDeleteId(record.id); setDeleteModal(true); }} />
        </div>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        variant="section"
        title="Uniform Items"
        actions={
          <Button
            label="Add Item"
            icon="fa fa-plus"
            className="btn btn-primary btn-sm"
            onClick={() => { setSelectedItem(null); setShowModal(true); }}
          />
        }
      >
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search items..."
          aria-label="Search uniform items"
          style={{ width: 200 }}
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPageSettings(prev => ({ ...prev, page: 1 })); }}
        />
      </PageHeader>

      <Table
        columns={columns}
        dataSource={data}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={{
          total: pageSettings.total,
          pageSize: pageSettings.pageSize,
          current: pageSettings.page,
          pageSizeOptions,
          itemRender,
          showSizeChanger: true
        }}
        onChange={handleChange}
        scroll={{ x: "max-content" }}
      />

      <UniformItemFormModal
        open={showModal}
        onClose={() => { setShowModal(false); setSelectedItem(null); }}
        selectedItem={selectedItem}
        onSuccess={handleSuccess}
      />

      <DeleteModal
        Name="Delete Uniform Item"
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteHandler={handleDelete}
      />
    </div>
  );
};

export default UniformItemList;
